/**
 * The tab strip's order — the layouts, with the journal among them.
 *
 * The journal is a tab with no layout behind it: no grid, no sections, and it
 * cannot be deleted. That makes it the one entry in the strip that cannot carry
 * its position in `workspace.layouts`, so the position lives on the workspace as
 * `journalIndex` and this module is the one place that knows how to read the two
 * together.
 *
 * **Pure and tested here, for `layout-templates.ts`'s reason** — the interesting
 * part of a drag is arithmetic, and arithmetic must not live in the one place
 * this app cannot test. `LayoutPanel` renders what `tabOrder` returns and sends
 * back two positions; nothing in a component decides what a move means.
 *
 * **Positions are into the full order, hidden entries included.** The panel and
 * the strip both filter what they draw, and both keep the true position on what
 * is left — renumbering around a hidden layout is what sends a drop to the wrong
 * place, and it is the same trap `LayoutBar` documents for `onSelect`.
 */

import type { Layout, UserWorkspace } from "@qufolio/model";

/** The journal's id in the order. Not a ULID, so it cannot collide with one. */
export const JOURNAL_TAB = "journal";

/** A layout's id, or the journal. */
export type TabId = string;

/** One tab, resolved: a layout at its index in `workspace.layouts`, or the journal. */
export type Tab =
  | { kind: "layout"; layout: Layout; index: number }
  | { kind: "journal" };

/**
 * Where the journal sits, clamped to somewhere it can actually be drawn.
 *
 * Absent means **last**, which is where it has always been — a workspace saved
 * before the position existed must not have its strip reordered by a deploy.
 *
 * Clamped rather than trusted, because deleting a layout shortens the strip
 * underneath a stored index. An index past the end would otherwise drop the
 * journal off the order entirely, which is a hidden tab that nothing can unhide.
 */
export function journalPosition(
  layouts: readonly Layout[],
  journalIndex: number | undefined,
): number {
  if (journalIndex === undefined) return layouts.length;
  return Math.min(Math.max(0, Math.trunc(journalIndex)), layouts.length);
}

/** Every tab in strip order, by id. Length is always `layouts.length + 1`. */
export function tabOrder(
  layouts: readonly Layout[],
  journalIndex: number | undefined,
): TabId[] {
  const ids = layouts.map((l) => l.id);
  ids.splice(journalPosition(layouts, journalIndex), 0, JOURNAL_TAB);
  return ids;
}

/** Every tab in strip order, resolved — what a renderer walks. */
export function tabStrip(
  layouts: readonly Layout[],
  journalIndex: number | undefined,
): Tab[] {
  const byId = new Map(layouts.map((l, index) => [l.id, index]));
  return tabOrder(layouts, journalIndex).map((id) => {
    const index = byId.get(id);
    return index === undefined
      ? ({ kind: "journal" } as const)
      : ({ kind: "layout", layout: layouts[index]!, index } as const);
  });
}

/**
 * One tab dragged from `from` to `to`, as positions in `tabOrder`.
 *
 * Returns the layouts in their new order and the journal's new position, which
 * is everything a caller has to write — and returns the **inputs unchanged**
 * when the move is a no-op, so a drop on the row you picked up costs no save.
 *
 * Both are derived from one spliced array rather than adjusted separately: a
 * reorder that computed the new `journalIndex` from the old one has to get the
 * direction of travel right, and that is exactly the arithmetic that is wrong
 * in half the drag implementations ever written.
 */
export function moveTab(
  layouts: readonly Layout[],
  journalIndex: number | undefined,
  from: number,
  to: number,
): { layouts: Layout[]; journalIndex: number } {
  const order = tabOrder(layouts, journalIndex);
  const last = order.length - 1;
  const start = Math.min(Math.max(0, from), last);
  const end = Math.min(Math.max(0, to), last);

  const moved = order.slice();
  const [picked] = moved.splice(start, 1);
  moved.splice(end, 0, picked!);

  const byId = new Map(layouts.map((l) => [l.id, l]));
  return {
    layouts: moved.filter((id) => id !== JOURNAL_TAB).map((id) => byId.get(id)!),
    journalIndex: moved.indexOf(JOURNAL_TAB),
  };
}

/**
 * Whether this workspace's journal tab is put away.
 *
 * A function rather than a read of the field, so the "absent and false are the
 * same thing" rule is written once — `isHidden` in `layout-visibility.ts` exists
 * for the same reason and this is its sibling.
 */
export function journalHidden(ws: Pick<UserWorkspace, "journalHidden">): boolean {
  return ws.journalHidden === true;
}
